import { AttributeKindEnum, TemplateAttributeRole } from "@brewbase/db/types";
/**
 * Attribute kinds that never count toward completeness:
 * - INSTRUCTION: display-only, not data.
 * - MEDIA_ATTACHMENT: optional by default.
 * - COMPUTED_NUMBER: derived from other attributes, which already count;
 *   counting it would double-count (and its errors are surfaced separately).
 */
const EXCLUDED_KINDS = new Set([
    AttributeKindEnum.INSTRUCTION,
    AttributeKindEnum.MEDIA_ATTACHMENT,
    AttributeKindEnum.COMPUTED_NUMBER,
]);
function hasScalarValue(v) {
    return v !== null && v !== undefined && v !== "";
}
/** Whether a single data-bearing attribute holds a value. */
function isAttributeFilled(attr) {
    switch (attr.kind) {
        case AttributeKindEnum.SINGLE_NUMBER:
            return hasScalarValue(attr.number_value);
        case AttributeKindEnum.CHECK:
            // false is a valid answer ("no"); only null/undefined is unfilled.
            return attr.check_value !== null && attr.check_value !== undefined;
        case AttributeKindEnum.TIMESTAMP:
        case AttributeKindEnum.DATESTAMP:
            return attr.timestamp_value !== null && attr.timestamp_value !== undefined;
        case AttributeKindEnum.TEXT:
            return typeof attr.text_value === "string" && attr.text_value.trim() !== "";
        case AttributeKindEnum.INVENTORY_ITEM_REFERENCE:
            return attr.inventory_item_id !== null && attr.inventory_item_id !== undefined;
        case AttributeKindEnum.TABULAR_DATA:
            // Complete iff it has at least one row (even an empty one).
            return Number(attr.tabular_row_count ?? 0) >= 1;
        default:
            return false;
    }
}
/**
 * Compute automatic data completeness for one operation's attributes.
 *
 * Every recorded-data attribute is required by default (an explicit "optional"
 * flag is deferred). Excluded kinds (instruction/media/computed) and PARAMETER
 * setpoints are ignored. Advisory only — never gates session completion.
 */
export function computeOperationCompleteness(attributes) {
    const counted = attributes.filter((a) => !EXCLUDED_KINDS.has(a.kind) &&
        a.role !== TemplateAttributeRole.PARAMETER);
    const total = counted.length;
    const filled = counted.reduce((n, a) => n + (isAttributeFilled(a) ? 1 : 0), 0);
    return {
        filled,
        total,
        fraction: total === 0 ? 1 : filled / total,
        isComplete: filled === total,
    };
}
//# sourceMappingURL=harvest-completeness.js.map